import { browser } from '$app/environment';
import type { LogPayload } from '$lib/api/velatlas/response.types';
import { ClientVelatlasLoggerApi } from '$lib/api/velatlas/velatlas.loggerApi.client';
import { ENABLE_LOGGING_TO_SERVER } from '$lib/statics';

export const DEDUPE_WINDOW_MS = 5000; // Anti-spam sliding window threshold
let isLoggingEnabled = ENABLE_LOGGING_TO_SERVER;
// Client-side error messages to ignore
const SPAM_ERROR_PATTERNS: RegExp[] = [
    /tiles\.openfreemap\.org/i // Ignore openfreemap vector tile download failures
];
const ERROR_UNWANTED_MESSAGES: string[] = [
    'The source image could not be decoded.',
    'Failed to fetch dynamically imported module',
    'WebGL2 is required to display this map.'
];

const recentLogs = new Map<string, number>(); // Anti-spam map tracks: { "error_message" -> timestamp }
const originalConsoleError = console.error;
const api = new ClientVelatlasLoggerApi();

function isSpam(message: string): boolean {
    const now = Date.now();
    const lastSentTime = recentLogs.get(message);

    for (const [key, timestamp] of recentLogs.entries()) {
        if (now - timestamp > DEDUPE_WINDOW_MS) {
            recentLogs.delete(key);
        }
    }

    const spamDetected = lastSentTime && now - lastSentTime < DEDUPE_WINDOW_MS;
    if (spamDetected) {
        return true;
    }

    recentLogs.set(message, now);
    return false;
}

function containsUnwantedMessage(message: string, unwantedMessages: string[]) {
    return unwantedMessages.some(unwantedMessage => message.toLowerCase().includes(unwantedMessage.toLowerCase()));
}

export const logger = {
    enable: () => { isLoggingEnabled = true; },
    disable: () => { isLoggingEnabled = false; },
    status: () => isLoggingEnabled,

    async error(
        message: string,
        stack?: string,
        metadata?: Record<string, any>,
        customUri?: string,
        customUrl?: string,
        customFetch?: typeof fetch,
        skipConsolePrint = false
    ) {
        if (browser && !skipConsolePrint) {
            if (stack) {
                originalConsoleError.call(console, message, stack);
            } else {
                originalConsoleError.call(console, message);
            }
        }

        const isSpamError = SPAM_ERROR_PATTERNS.some(pattern => pattern.test(message));
        if (isSpamError || !isLoggingEnabled || isSpam(message)) return;

        let finalMessage = message;
        if (metadata && typeof metadata === 'object') {
            finalMessage += ' ' + JSON.stringify(metadata);
        }
        const shouldSendLog = !containsUnwantedMessage(finalMessage, ERROR_UNWANTED_MESSAGES);
        if (!shouldSendLog) return;

        const meta = ClientVelatlasLoggerApi.getRouteMetadata();
        const payload: LogPayload = {
            type: 'ERROR',
            message: finalMessage,
            stack,
            uri: customUri || meta.uri,
            url: customUrl || meta.url
        };

        await api.sendLog(payload, customFetch);
    },

    async warn(
        message: string,
        metadata?: Record<string, any>,
        customUri?: string,
        customUrl?: string,
        customFetch?: typeof fetch
    ) {
        let finalMessage = message;
        if (metadata && typeof metadata === 'object') {
            finalMessage += ' ' + JSON.stringify(metadata);
        }

        if (browser) {
            console.warn(finalMessage);
        }

        if (!isLoggingEnabled || isSpam(message)) return;

        const meta = ClientVelatlasLoggerApi.getRouteMetadata();
        const payload: LogPayload = {
            type: 'WARNING',
            message: finalMessage,
            uri: customUri || meta.uri,
            url: customUrl || meta.url
        };

        await api.sendLog(payload, customFetch);
    }
};

if (browser) {
    console.error = (...args: any[]) => {
        originalConsoleError.apply(console, args);

        const errorInstance = args.find((arg) => arg instanceof Error);
        const stack = errorInstance ? errorInstance.stack : undefined;

        // Map arguments to messages cleanly without appending the stack to the message
        const message = args
            .map((arg) => {
                if (arg instanceof Error) {
                    return arg.message;
                }
                return typeof arg === 'object' ? JSON.stringify(arg) : String(arg);
            })
            .join(' ');

        // Pass the separated message and stack directly to the logger
        logger.error(message, stack, undefined, undefined, undefined, undefined, true);
    };

    // Handle standard runtime uncaught exceptions
    window.addEventListener('error', (event: ErrorEvent) => {
        logger.error(event.message, event.error?.stack);
    });

    // Handle runtime unhandled async promise rejections
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const message = event.reason instanceof Error ? event.reason.message : String(event.reason);
        const stack = event.reason instanceof Error ? event.reason.stack : undefined;
        logger.error(`Unhandled Rejection: ${message}`, stack);
    });
}